/**
 * Portfolios Page — standalone portfolio manager accessible from the nav sidebar.
 * Allows viewing, loading, renaming and deleting saved lookbook portfolios
 * without requiring an active Lookbook session.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Paper,
  IconButton,
  Button,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import { db, type SavedLookbookRow } from '../shared/services/db';
import { LOOKBOOK_CONTEXT_KEY, type LookbookContext } from './CataloguePage';

export const PortfoliosPage: React.FC = () => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState<SavedLookbookRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  useEffect(() => {
    db.savedLookbooks
      .toArray()
      .then(rows => {
        setPortfolios(rows.sort((a, b) => b.createdAt - a.createdAt));
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLoad = useCallback((row: SavedLookbookRow) => {
    const ctx: LookbookContext = {
      presentationSetIds: row.itemIds,
      showIngredients: row.showIngredients,
      airlineMode: row.airlineMode,
      showPrices: row.showPrices,
      tierEntries: row.tierEntries,
      timestamp: Date.now(),
    };
    localStorage.setItem(LOOKBOOK_CONTEXT_KEY, JSON.stringify(ctx));
    navigate('/lookbook');
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    await db.savedLookbooks.delete(id);
    setPortfolios(prev => prev.filter(r => r.id !== id));
  }, []);

  const handleRename = useCallback(async (id: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    await db.savedLookbooks.where('id').equals(id).modify({ name: trimmed });
    setPortfolios(prev => prev.map(r => r.id === id ? { ...r, name: trimmed } : r));
    setRenamingId(null);
  }, []);

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <FolderSpecialIcon sx={{ color: '#A55DA0', fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#363D4C' }}>
          My Portfolios
        </Typography>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress sx={{ color: '#A55DA0' }} />
        </Box>
      ) : portfolios.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <FolderSpecialIcon sx={{ fontSize: 48, color: '#ddd', mb: 1 }} />
          <Typography color="text.secondary">
            No saved portfolios yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select products in the Product List, open the Lookbook, and save a portfolio from there.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {portfolios.map(lb => (
            <Paper
              key={lb.id}
              variant="outlined"
              sx={{ borderRadius: 2, overflow: 'hidden', '&:hover': { borderColor: '#A55DA0' } }}
            >
              {renamingId === lb.id ? (
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleRename(lb.id, renameValue);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      autoFocus
                      label="Portfolio name"
                    />
                    <IconButton onClick={() => handleRename(lb.id, renameValue)} color="primary">
                      <CheckIcon />
                    </IconButton>
                    <IconButton onClick={() => setRenamingId(null)}>
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, color: '#363D4C', mb: 0.5 }}>
                        {lb.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={`${lb.itemIds.length} products`}
                          size="small"
                          sx={{ bgcolor: '#EDE5ED', color: '#A55DA0', fontWeight: 600 }}
                        />
                        {lb.airlineMode && (
                          <Chip label="Airline mode" size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        )}
                        {lb.showPrices && (
                          <Chip label="Prices" size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Saved {new Date(lb.createdAt).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })}
                        </Typography>
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={0.5} flexShrink={0}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<MenuBookIcon />}
                        onClick={() => handleLoad(lb)}
                        sx={{
                          bgcolor: '#A55DA0',
                          '&:hover': { bgcolor: '#8a4d87' },
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Open Lookbook
                      </Button>
                      <Tooltip title="Rename">
                        <IconButton
                          size="small"
                          onClick={() => { setRenamingId(lb.id); setRenameValue(lb.name); }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(lb.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              )}
              <Divider />
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PortfoliosPage;
